import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Settings, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
    functional: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('playmatez_cookie_consent');
    if (!consent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('playmatez_cookie_consent', JSON.stringify(allAccepted));
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('playmatez_cookie_consent', JSON.stringify(onlyNecessary));
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    const savedPreferences = {
      ...preferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('playmatez_cookie_consent', JSON.stringify(savedPreferences));
    setIsVisible(false);
    setShowPreferences(false);
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return; // Can't disable necessary cookies
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const cookieTypes = [
    {
      key: 'necessary',
      title: 'Strictly Necessary',
      description: 'Essential for the website to function properly. These cookies cannot be disabled.',
      required: true,
    },
    {
      key: 'functional',
      title: 'Functional',
      description: 'Enable personalized features and remember your preferences.',
      required: false,
    },
    {
      key: 'analytics',
      title: 'Analytics',
      description: 'Help us understand how visitors interact with our website to improve user experience.',
      required: false,
    },
    {
      key: 'marketing',
      title: 'Marketing',
      description: 'Used to deliver relevant advertisements and track campaign effectiveness.',
      required: false,
    },
  ];

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6"
          data-testid="cookie-banner"
        >
          <div className="max-w-4xl mx-auto">
            <div className="bg-card border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
              {!showPreferences ? (
                // Main Banner
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Cookie className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-lg font-bold text-white mb-2">
                        We value your privacy
                      </h3>
                      <p className="text-muted-foreground text-sm mb-4">
                        We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
                        By clicking "Accept All", you consent to our use of cookies. Read our{' '}
                        <Link to="/privacy-policy" className="text-primary hover:underline">
                          Privacy Policy
                        </Link>{' '}
                        to learn more.
                      </p>
                      <div className="flex flex-wrap items-center gap-3">
                        <button
                          onClick={handleAcceptAll}
                          className="px-5 py-2.5 bg-primary text-white font-semibold rounded-xl hover:bg-primary/90 transition-colors"
                          data-testid="cookie-accept-all"
                        >
                          Accept All
                        </button>
                        <button
                          onClick={handleRejectAll}
                          className="px-5 py-2.5 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
                          data-testid="cookie-reject-all"
                        >
                          Reject All
                        </button>
                        <button
                          onClick={() => setShowPreferences(true)}
                          className="px-5 py-2.5 text-muted-foreground hover:text-white font-medium transition-colors flex items-center gap-2"
                          data-testid="cookie-customize"
                        >
                          <Settings className="w-4 h-4" />
                          Customize
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                // Preferences Panel
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Settings className="w-5 h-5 text-primary" />
                      </div>
                      <h3 className="font-heading text-lg font-bold text-white">
                        Cookie Preferences
                      </h3>
                    </div>
                    <button
                      onClick={() => setShowPreferences(false)}
                      className="p-2 text-muted-foreground hover:text-white transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="space-y-4 mb-6">
                    {cookieTypes.map((cookie) => (
                      <div
                        key={cookie.key}
                        className="flex items-start gap-4 p-4 bg-white/5 rounded-xl"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-white">{cookie.title}</h4>
                            {cookie.required && (
                              <span className="text-xs px-2 py-0.5 bg-primary/20 text-primary rounded-full">
                                Required
                              </span>
                            )}
                          </div>
                          <p className="text-muted-foreground text-sm">{cookie.description}</p>
                        </div>
                        <button
                          onClick={() => togglePreference(cookie.key)}
                          disabled={cookie.required}
                          className={`relative w-12 h-6 rounded-full transition-colors ${
                            preferences[cookie.key]
                              ? 'bg-primary'
                              : 'bg-white/20'
                          } ${cookie.required ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          data-testid={`cookie-toggle-${cookie.key}`}
                        >
                          <div
                            className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                              preferences[cookie.key] ? 'left-7' : 'left-1'
                            }`}
                          />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-end gap-3">
                    <button
                      onClick={handleRejectAll}
                      className="px-5 py-2.5 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
                    >
                      Reject All
                    </button>
                    <button
                      onClick={handleSavePreferences}
                      className="px-5 py-2.5 bg-primary text-white font-semibold rounded-xl hover:bg-primary/90 transition-colors flex items-center gap-2"
                      data-testid="cookie-save-preferences"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Save Preferences
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
